/**
 * Builds the Organizations snapshots (screenshots/56–65) from the public site's lobbying files, so the
 * dashboard and the public client pages count money the same way (brief §2.10: company + employees +
 * registered lobbyists, with the overlap note). Pure: the Worker (worker/src/dashboard-orgs.js) feeds it
 * the files from ASSETS plus D1/KV extras; the preview feeds it the same files from disk.
 *
 * Positions (brief §2.10, §8):
 *   stated   = the public file's filing-based stance is support/oppose (a filing names the record with a position)
 *   inferred = no stated stance, but the producer inferred one from named proposals (KV orgs:inferred:<slug>)
 *   unclear  = neither. "Position unclear" is never shown as "Opposes".
 */
import { evidenceOf, type ForecastInput, type Position } from "../forecast/forecast";
import type { ClientProfile, ClientSummary, Filing, FollowedClient, FundedMember, LobbiedItem, LobbyistSummary, OrgTotals, PositionBasis, ProfileLobbyist } from "./types";
import { clientMoneyFromFile, fileSplit } from "../money/ledgerInputs";

// ---------- the public files (scripts/money_links.py + scripts/lobby_profiles.py) ----------
export interface PublicIndex {
  generated: string;
  clients: { slug: string; name: string; filings: number; legislation: number; toMembers: number }[];
  lobbyists: { slug: string; name: string; firm: string; filings: number; toMembers: number }[];
}
export interface PublicClient {
  name: string; slug: string; filings: number; lobbyists: number; legislationLobbied: number;
  money: { fromCompany: number; fromEmployees: number; fromLobbyists: number; total: number; membersFunded: number };
  memberMoney?: { memberId: number; name: string; amount: number; company: number; employees: number; lobbyists: number }[];
  seekingAll?: { text: string; department: string; period: string }[];
  legislation?: { id: string; record: string; title: string; filings: number }[];
  lobbyistsOnLegislation?: { id: string; slug: string; name: string }[];
  interests?: { coreInterest?: string; objectives?: { title: string; detail: string }[]; affects?: { group: string; how: string }[];
    stances?: { record: string; stance: "support" | "oppose" | "unclear"; evidence: string }[] };
}
export interface PublicLobbyist {
  id: string; name: string; slug: string; firm: string; filings: number; clients: number; someClients?: string[];
  toMembers: number; membersFunded: number; topMembers?: { memberId: number; name: string; amount: number }[];
  clientPages?: { slug: string; name: string }[];
}
/** site/data/alderpersons.json, by ward. */
export type Alders = Record<string, { ward: number; name: string; photo?: string | null }>;
/** site/data/legislation.json rows (only the fields used for topics). */
export interface LegRow { record: string; title: string; category?: string | null; committee?: string | null }

// ---------- extras from producers (KV) and D1 ----------
/** KV orgs:meta → { <slug>: OrgMeta } (AI, from filings; missing until the producer runs). */
export interface OrgMeta { kind?: string; subject?: string; latest?: string }
/** KV orgs:inferred:<slug> → positions inferred from named proposals. */
/** `basis: "stated"` = a filing names the record number and states the position (worker/src/producers/filings.js). */
export interface InferredPosition { record: string; position: Position; namedProposals: string[]; quote?: string; basis?: "stated" }
export interface ProfileExtras {
  meta?: OrgMeta;
  inferred?: InferredPosition[];
  /** record → short plain title (KV legislation:plain:<matterId>.shortTitle). */
  shortTitles?: Record<string, string>;
  /** record → how the filings were matched (ANALYSIS lobby_matter_links.method). */
  matchMethods?: Record<string, string>;
  /** Filings from MONEY lobby_activity (newest first), when the DB is reachable; else the file's seekingAll. */
  filings?: { id: string; text: string; department: string; period: string; lobbyistId: string; records?: { record: string; method: string }[] }[];
  /** Next agenda per record ("City Council Oct 14"). */
  nextVote?: Record<string, string>;
  override?: Record<string, { position: Position; by: string }>;
}

const SINCE = "2019";
const round = (n: number) => Math.round(n || 0);

/** The people id the People section uses for a sitting alderperson ("a:39"); former members have none. */
export const memberPersonId = (memberId: number) => (memberId >= 1 && memberId <= 50 ? `a:${memberId}` : "");
const photoOf = (alders: Alders, memberId: number) => {
  const u = alders[String(memberId)]?.photo;
  return u ? u.replace(/^https:\/\/www\.chicagopolitics\.org/, "") : null;
};

/** "Chicagoland Apartment Association" → "CAA"; "Airbnb, Inc." → "Airbnb". */
export function shortName(name: string) {
  const words = name.replace(/[,.]?\s+(Inc|LLC|Ltd|L\.?P|Co|Corp)\.?$/i, "").split(/\s+/);
  if (words.length <= 2) return words.join(" ");
  return words.filter((w) => /^[A-Z]/.test(w) && !/^(Of|The|And|For)$/i.test(w)).map((w) => w[0]).join("").toUpperCase();
}

/** Two-letter tile ("CA", "1A", "O"), as in the design's logo tiles. */
export const initials = (n: string) => n.replace(/^The /, "").split(/\s+/).filter((w) => /^[A-Z0-9]/i.test(w) && /^[A-Z0-9]/.test(w)).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/** Public names come title-cased from the City's data ("Of Metropolitan"); keep small words lower. */
export const tidyName = (n: string) => n.replace(/\s*\/\s*/g, " / ").replace(/\b(Of|And|For|The|In|On|At)\b/g, (w, _m, i) => (i === 0 ? w : w.toLowerCase())).replace(/, Llc\b/g, ", LLC").replace(/\bLlc\b/g, "LLC").replace(/\bLp\b/g, "LP").replace(/\bLlp\b/g, "LLP").replace(/\bUsa\b/g, "USA").replace(/'T\b/g, "'t");

/** "City Council/Alderpersons" → "City Council / Alderpersons"; "Buildings" → "Department of Buildings". */
export function agencyName(dep: string) {
  const d = (dep || "").trim();
  if (!d) return "";
  if (d.includes("/")) return d.replace(/\s*\/\s*/g, " / ");
  const t = tidyName(d).replace(/^Dept\.? of /i, "Department of ");
  return /^(Buildings|Housing|Planning( and Development)?|Transportation|Finance|Law|Aviation|Water Management|Public Health|Environment|Streets and Sanitation|Business Affairs and Consumer Protection)$/i.test(t) ? `Department of ${t}` : t;
}

// ---------- topics ("Your issues") ----------
const TOPIC_RULES: [string, RegExp][] = [
  ["Housing", /HOUSING|RENTAL|TENANT|LANDLORD|DWELLING|HOMELESS|\bADU\b/i],
  ["Zoning", /ZONING|TITLE 17|LAND USE/i],
  ["Budget & revenue", /BUDGET|TAX|REVENUE|BOND/i],
  ["Public safety", /POLICE|PUBLIC SAFETY|FIRE/i],
  ["Licenses & permits", /LICENSE|PERMIT|VENDING|VENDOR/i],
  ["Transportation", /PARKING|TRANSPORT|TRAFFIC|STREET/i],
  ["Environment", /ENVIRONMENT|CLIMATE|ENERGY|GAS\b|WATER/i],
  ["Short-term rentals", /SHORT[- ]TERM RENTAL|SHARED HOUSING|VACATION RENTAL/i],
];
/** Topics of the items a client lobbied, plus any topic at least two of its filings mention. */
export function topicsOf(c: PublicClient, legByRecord: Map<string, LegRow>): string[] {
  const out = new Set<string>();
  for (const l of c.legislation ?? []) {
    const r = legByRecord.get(l.record);
    const text = `${l.title} ${r?.category ?? ""} ${r?.committee ?? ""}`;
    for (const [t, re] of TOPIC_RULES) if (re.test(text)) out.add(t);
  }
  for (const [t, re] of TOPIC_RULES) if ((c.seekingAll ?? []).filter((f) => re.test(f.text)).length >= 2) out.add(t);
  return [...out];
}

// ---------- positions ----------
export function positionOf(record: string, c: PublicClient, inferred: InferredPosition[] = []): { position: Position | null; basis: PositionBasis; namedProposals?: string[]; quote?: string } {
  const s = c.interests?.stances?.find((x) => x.record === record);
  if (s && (s.stance === "support" || s.stance === "oppose")) return { position: s.stance, basis: "stated" };
  const inf = inferred.find((x) => x.record === record);
  if (inf?.basis === "stated") return { position: inf.position, basis: "stated" };
  if (inf) return { position: inf.position, basis: "inferred", namedProposals: inf.namedProposals, ...(inf.quote ? { quote: inf.quote } : {}) };
  return { position: null, basis: "unclear" };
}

const METHOD: Record<string, LobbiedItem["matchedBy"]> = { record_number: "record", street_address: "address", client_named: "client", semantic_embedding: "wording", semantic_tfidf: "wording" };
/** Record numbers in filing text; filers often type a zero for the "O" ("02026-0026554"). */
export function recordsIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/\b(SO|S0|O|0|R|SR|F|A)(\d{4})-(\d{7})\b/gi)) out.push(`${m[1].toUpperCase().replace(/0/g, "O")}${m[2]}-${m[3]}`);
  return out;
}

// ---------- list (screenshots/56–59) ----------
export function clientSummary(c: PublicClient, legByRecord: Map<string, LegRow>, meta: OrgMeta = {}): ClientSummary {
  return {
    id: c.slug, name: tidyName(c.name), kind: meta.kind ?? "", legislationCount: c.legislationLobbied, filings: c.filings,
    money: fileSplit(c.money),   // the one money rule (money/moneyLedger.ts), as the producer applied it
    alderpersonsFunded: c.money.membersFunded, topics: topicsOf(c, legByRecord), lobbyistCount: c.lobbyists,
  };
}

export function lobbyistSummary(l: PublicLobbyist): LobbyistSummary {
  return { id: l.slug, name: l.name, firm: tidyName(l.firm || ""), filings: l.filings, clients: l.clients, toAlderpersons: round(l.toMembers),
    alderpersonsFunded: l.membersFunded,
    clientNames: [...new Set([...(l.clientPages ?? []).map((c) => tidyName(c.name)), ...(l.someClients ?? []).map(tidyName)])] };
}

export function totals(index: PublicIndex, clients: PublicClient[]): OrgTotals {
  return {
    clients: index.clients.length, lobbyists: index.lobbyists.length,
    filings: index.clients.reduce((s, c) => s + (c.filings || 0), 0),
    // The sum of client totals, as the public index counts it (lobbyists' giving overlaps across clients).
    toAlderpersons: round(clients.reduce((s, c) => s + (c.money?.total || 0), 0)),
    since: SINCE,
  };
}

/** Lobbyists of the clients on your issues, most money first (Lobbyists tab side card + pinned rows). */
export function lobbyistsOnIssues(clients: PublicClient[], onIssues: Set<string>, lobbyists: Map<string, LobbyistSummary>): string[] {
  const ids = new Set<string>();
  for (const c of clients) if (onIssues.has(c.slug)) for (const l of c.lobbyistsOnLegislation ?? []) if (lobbyists.has(l.slug)) ids.add(l.slug);
  return [...ids].map((id) => lobbyists.get(id)!).sort((a, b) => b.toAlderpersons - a.toAlderpersons || b.filings - a.filings).slice(0, 3).map((l) => l.id);
}

const sentence = (s: string) => (s && s === s.toUpperCase() ? s[0] + s.slice(1).toLowerCase() : s);
const monDay = (iso: string) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" }) : "");

/** A followed client's card (Following tab, screenshots/58). */
/** Cards show stated positions only; an inferred one stays on the item it was inferred for. */
export function followedCard(c: PublicClient, summary: ClientSummary, meta: OrgMeta = {}): FollowedClient {
  const stated = (c.interests?.stances ?? []).filter((s) => s.stance === "support" || s.stance === "oppose");
  if (stated.length) {
    const position = stated.filter((s) => s.stance === "oppose").length >= stated.length / 2 ? "oppose" : "support";
    const recs = stated.filter((s) => s.stance === position).map((s) => s.record);
    return { ...summary, position, basis: "stated", subject: meta.subject, latest: `Stated in filings on ${recs.length > 1 ? `${recs.slice(0, -1).join(", ")} and ${recs.at(-1)}` : recs[0]}` };
  }
  const last = c.seekingAll?.[0];
  return { ...summary, position: null, basis: "unclear",
    latest: meta.latest ?? (last ? `Latest: ${sentence(last.text).replace(/\.$/, "")} · ${monDay(last.period)}` : "") };
}

// ---------- profile (screenshots/60–65) ----------
export function clientProfile(c: PublicClient, ctx: {
  legByRecord: Map<string, LegRow>; alders: Alders; lobbyists: Map<string, PublicLobbyist>; extras?: ProfileExtras;
  /** Lower-cased client name → its total to alderpersons (index.json): "also lobbies for" lists the biggest first. */
  clientTotals?: Map<string, number>;
}): ClientProfile {
  const x = ctx.extras ?? {};
  const base = clientSummary(c, ctx.legByRecord, x.meta);
  const items: LobbiedItem[] = (c.legislation ?? []).map((l) => {
    const p = positionOf(l.record, c, x.inferred);
    const s = c.interests?.stances?.find((st) => st.record === l.record);
    return {
      recordId: l.record, title: x.shortTitles?.[l.record] ?? l.title, filings: l.filings,
      position: p.position, basis: p.basis, evidence: s?.evidence ?? "", matchedBy: METHOD[x.matchMethods?.[l.record] ?? ""] ?? "record",
      ...(p.namedProposals ? { namedProposals: p.namedProposals } : {}), ...(p.quote ? { inferredQuote: p.quote } : {}),
      override: x.override?.[l.record] ?? null, next: x.nextVote?.[l.record] ?? null,
    };
  });
  // Per alderperson through the one money rule (money/moneyLedger.ts): company + employees + its lobbyists' own gifts.
  const ledger = clientMoneyFromFile(c.slug, c.memberMoney ?? [], (c.lobbyistsOnLegislation ?? []).map((l) => l.id));
  const funded: FundedMember[] = ledger.byRecipient.map((r) => {
    const memberId = Number(r.personId.slice("member:".length));
    return { personId: memberPersonId(memberId), name: r.name, ward: r.ward!, amount: round(r.total), companyOnly: round(r.company + r.employees), photoUrl: photoOf(ctx.alders, memberId) };
  }).sort((a, b) => b.amount - a.amount);
  const lobbyists: ProfileLobbyist[] = (c.lobbyistsOnLegislation ?? []).map((ref) => {
    const l = ctx.lobbyists.get(ref.slug);
    const sum = l ? lobbyistSummary(l) : { id: ref.slug, name: ref.name, firm: "", filings: 0, clients: 1, toAlderpersons: 0, alderpersonsFunded: 0 };
    const big = (n: string) => ctx.clientTotals?.get(n.toLowerCase()) ?? 0;
    const others = (sum.clientNames ?? []).filter((n) => n.toLowerCase() !== tidyName(c.name).toLowerCase());
    return { ...sum, otherClients: others.map((n, i) => ({ n, i })).sort((a, b) => big(b.n) - big(a.n) || a.i - b.i).map((x) => x.n),
      topMembers: (l?.topMembers ?? []).map((m) => ({ personId: memberPersonId(m.memberId), name: m.name, amount: round(m.amount) })) };
  }).sort((a, b) => b.toAlderpersons - a.toAlderpersons || b.filings - a.filings);
  const known = new Set(items.map((i) => i.recordId));
  const rawFilings = x.filings ?? (c.seekingAll ?? []).map((f, i) => ({ id: `f${i}`, text: f.text, department: f.department, period: f.period, lobbyistId: "", records: undefined }));
  const filingsRecent: Filing[] = rawFilings.map((f) => ({
    id: f.id, quarterEnding: f.period.slice(0, 10), agency: agencyName(f.department), lobbyistId: f.lobbyistId, text: f.text,
    matchedRecords: f.records?.length
      ? f.records.map((r) => ({ recordId: r.record, matchedBy: METHOD[r.method] ?? "wording" }))
      : recordsIn(f.text).map((r) => (known.has(r) ? r : known.has(`S${r}`) ? `S${r}` : null)).filter((r): r is string => !!r)
        .map((recordId) => ({ recordId, matchedBy: "record" as const })),
  })).sort((a, b) => b.quarterEnding.localeCompare(a.quarterEnding));
  // Votes & forecast: the item with a position (stated first, then inferred), preferring one with a vote coming up.
  const ranked = items.filter((i) => i.basis !== "unclear").sort((a, b) => (a.basis === "stated" ? 0 : 1) - (b.basis === "stated" ? 0 : 1) || (b.next ? 1 : 0) - (a.next ? 1 : 0));
  const pick = ranked[0];
  return {
    ...base, interests: c.interests?.coreInterest ?? "",
    asks: (c.interests?.objectives ?? []).map((o) => ({ title: o.title, detail: o.detail })),
    affects: (c.interests?.affects ?? []).map((a) => ({ group: a.group, detail: a.how })),
    items, lobbyists, funded, filingsRecent,
    ...(pick ? { forecastItem: { recordId: pick.recordId, title: pick.title, position: pick.position, basis: pick.basis, next: pick.next ?? null } } : {}),
  };
}

// ---------- forecast inputs (Votes & forecast tab) ----------
/** One member's row of the forecast producer's inputs (KV forecast-inputs:<matterId>, see worker/src/legislation.js). */
export type MemberInputs = { personId: string; similar?: { yesRate: number; n: number }; moneyLinked?: { k: number; n: number }; memberLean?: number; councilYesRate?: number } & Partial<ForecastInput>;

/**
 * forecastVote() inputs for one member on one item, or null without enough history (fewer than 3 similar items).
 * Money-linked votes need a STATED position: with an inferred position (or a team override) they are zeroed,
 * so the forecast leans on similar items, leaning and council tension instead (brief §2.10).
 */
export function memberInputs(item: Pick<LobbiedItem, "basis" | "position" | "override">, x: MemberInputs, billLean: number, tension: number): ForecastInput | null {
  const position = item.override?.position ?? item.position;
  if (!position || item.basis === "unclear" || !x.similar || x.similar.n < 3) return null;
  const stated = item.basis === "stated" && !item.override;
  return { position, similar: x.similar, moneyLinked: stated ? x.moneyLinked ?? { k: 0, n: 0 } : { k: 0, n: 0 }, memberLean: x.memberLean ?? 0, billLean, tension, councilYesRate: x.councilYesRate ?? 0.5, ...evidenceOf(x) };
}
